/// <reference path="./lucide-icons.d.ts" />
/**
 * The house icon seam. EVERY icon rendered anywhere in this package comes from
 * here, so the 1.25 stroke the directive asked for (Laith, 2026-08-14) cannot
 * drift one component at a time.
 *
 * LUCIDE, since 2026-09-07 (Laith: "shift to Lucide icons, but the same 1.25
 * stroke"). Tabler was the set from 2026-08-14 to that date; the public surface of
 * this module did not move with the swap — same sixteen names, same `FfnIconProps`,
 * same default size, same aria semantics — so no call site changed. The crop audit
 * of the same day found eight controls still drawing TEXT glyphs (▦ ▾ ▸ ‹ › ✓ × ▲ ▼
 * ↕ ?) beside the icon set; five names were added to retire them — Calendar for the
 * date-picker triggers, ArrowUp/ArrowDown/ChevronsUpDown for the table sort slot,
 * HelpCircle for the settings help affordance — and the chevrons, check and x the
 * seam already had took the rest. Twenty-one names now.
 *
 * THE STROKE IS IN VIEWBOX UNITS, NOT SCREEN PIXELS. Lucide draws on a 24-unit box
 * and takes `strokeWidth`; passed WITHOUT `absoluteStrokeWidth`, the value lands
 * verbatim as the svg's `stroke-width` attribute and scales with `size` exactly as
 * Tabler's `stroke` prop did (12px icon → 0.625px of ink, 24px → 1.25px). With
 * `absoluteStrokeWidth` Lucide would rescale it to 1.25 SCREEN px at every size — a
 * heavier 12px glyph and a lighter 36px one — which is a different look, not the
 * same one. Lucide's own `stroke` prop is the COLOUR, which is why the type below
 * spells the width prop out.
 *
 * PER-ICON DEEP IMPORTS, and the one sanctioned exception to the house `.js`
 * intra-package rule. `lucide-react` is ~42 MB unpacked across ~2,070 icon modules
 * and publishes NO `exports` map, so `lucide-react/dist/esm/icons/<kebab>.mjs` is
 * the one deep path that resolves (`lucide-react/icons/<name>` is not a file —
 * TS2307). The barrel is a built artifact with `sideEffects: false`, so a bundler
 * COULD shake it — but per-icon paths make the light entry structural instead of
 * dependent on the bundler doing its best, the same decision the Tabler seam
 * recorded. The `.mjs` files carry no types beside them (TS7016);
 * `icons/lucide-icons.d.ts` supplies them, the same move `css.d.ts` makes for
 * component stylesheets.
 *
 * NAME MAP, Tabler → Lucide, kept so the export names (which are Tabler's) still
 * read against the module each one draws. Where Lucide offered two candidates the
 * pick is the one whose FIGURE matches the Tabler glyph it replaced:
 *   IconMaximize   → maximize     four corner brackets, as Tabler drew it; NOT
 *                                 maximize-2 (a pair of diagonal arrows)
 *   IconMinimize   → minimize     four corners pointing inward; NOT minimize-2
 *                                 (diagonal arrows meeting)
 *   IconMenu2      → menu         three equal bars — Tabler's "2" variant is
 *                                 Lucide's only one
 *   IconHelpCircle → circle-question-mark
 *                                 Lucide RENAMED `help-circle` → `circle-help` →
 *                                 `circle-question-mark`; the two older names still
 *                                 ship in 1.41.0 as one-line alias modules that
 *                                 re-export this one, and the svg stamps the
 *                                 canonical class `lucide-circle-question-mark`
 *                                 whichever path is imported. The canonical module is
 *                                 imported so the class the test asserts and the
 *                                 file on disk say the same thing.
 *   every other name maps to its kebab-case self (check, x, quote, sun, moon,
 *   search, upload, minus, plus, calendar, chevron-*, chevrons-up-down, arrow-up,
 *   arrow-down, arrow-*-right).
 *
 * Lucide is ISC (license inventory §8b).
 */

import ArrowDownBase from "lucide-react/dist/esm/icons/arrow-down.mjs"
import ArrowDownRightBase from "lucide-react/dist/esm/icons/arrow-down-right.mjs"
import ArrowUpBase from "lucide-react/dist/esm/icons/arrow-up.mjs"
import ArrowUpRightBase from "lucide-react/dist/esm/icons/arrow-up-right.mjs"
import CalendarBase from "lucide-react/dist/esm/icons/calendar.mjs"
import CheckBase from "lucide-react/dist/esm/icons/check.mjs"
import ChevronDownBase from "lucide-react/dist/esm/icons/chevron-down.mjs"
import ChevronLeftBase from "lucide-react/dist/esm/icons/chevron-left.mjs"
import ChevronRightBase from "lucide-react/dist/esm/icons/chevron-right.mjs"
import ChevronsUpDownBase from "lucide-react/dist/esm/icons/chevrons-up-down.mjs"
import CircleQuestionMarkBase from "lucide-react/dist/esm/icons/circle-question-mark.mjs"
import MaximizeBase from "lucide-react/dist/esm/icons/maximize.mjs"
import MenuBase from "lucide-react/dist/esm/icons/menu.mjs"
import MinimizeBase from "lucide-react/dist/esm/icons/minimize.mjs"
import MinusBase from "lucide-react/dist/esm/icons/minus.mjs"
import MoonBase from "lucide-react/dist/esm/icons/moon.mjs"
import PlusBase from "lucide-react/dist/esm/icons/plus.mjs"
import QuoteBase from "lucide-react/dist/esm/icons/quote.mjs"
import SearchBase from "lucide-react/dist/esm/icons/search.mjs"
import SunBase from "lucide-react/dist/esm/icons/sun.mjs"
import UploadBase from "lucide-react/dist/esm/icons/upload.mjs"
import XBase from "lucide-react/dist/esm/icons/x.mjs"
import type { ComponentType, ReactElement } from "react"
import { createElement } from "react"

/** The stroke the directive pins. One constant, one place to read it from. */
const HOUSE_STROKE = 1.25

/** Matches the 16px box the glyphs it replaces occupied at their font sizes. */
const DEFAULT_SIZE = 16

/**
 * The seam's prop surface — deliberately CLOSED, three props wide. Lucide's own
 * `LucideProps` is the whole `<svg>` surface plus `size`/`absoluteStrokeWidth`,
 * which would let a call site pass `strokeWidth={3}` and quietly undo the
 * directive. Size varies by site (FileDropzone wants 24), className is how a
 * component places the icon in its own grid, and aria-label is the escape hatch
 * for the one case where an icon is NOT decoration inside an already-labelled
 * control.
 */
export interface FfnIconProps {
  /** Square edge in px. @default 16 */
  readonly size?: number
  /** Merged onto the svg alongside Lucide's own `lucide lucide-<name>` classes. */
  readonly className?: string
  /**
   * Names the icon when it is the only thing carrying the meaning. Passing it
   * flips the icon from decoration (`aria-hidden`) to `role="img"` with a name —
   * the two are mutually exclusive, and a named-but-hidden icon is invisible to
   * assistive tech no matter how good the string is.
   */
  readonly "aria-label"?: string
}

type FfnIcon = (props: FfnIconProps) => ReactElement

/**
 * The subset of Lucide's svg prop surface the seam actually sets. Narrower than
 * the ambient `LucideIcon` on purpose — `ComponentType` because a Lucide icon is
 * a `forwardRef` exotic component, not a plain function, and a hand-rolled call
 * signature does not accept one. `strokeWidth`, not `stroke`: in Lucide the
 * latter is the colour.
 */
type LucideGlyph = ComponentType<{
  readonly size: number
  readonly strokeWidth: number
  readonly className?: string
  readonly focusable: false
  readonly "aria-hidden"?: true
  readonly "aria-label"?: string
  readonly role?: "img"
}>

/**
 * Wraps one Lucide icon into a house icon: stroke pinned, sized to 16, and
 * aria-hidden + focusable={false} by default because at every swap site the
 * icon sits inside a control that already has an accessible name (a Calendar nav
 * button, a Checkbox, a Select item). `focusable={false}` is for IE-era svg
 * focusability that lingers in some AT; harmless everywhere else. Lucide would
 * add `aria-hidden` itself when no a11y prop is present; the seam sets it
 * explicitly so the contract is written here rather than inherited.
 *
 * `strokeWidth` is applied AFTER nothing — it is not forwardable at all. That is
 * the point: the only way to change the stroke is to edit HOUSE_STROKE, which
 * `tests/icons.test.tsx` guards.
 */
function ffnIcon(Icon: LucideGlyph): FfnIcon {
  return ({ size = DEFAULT_SIZE, className, "aria-label": ariaLabel }: FfnIconProps) =>
    createElement(Icon, {
      size,
      strokeWidth: HOUSE_STROKE,
      focusable: false,
      ...(className !== undefined ? { className } : {}),
      ...(ariaLabel !== undefined
        ? { role: "img" as const, "aria-label": ariaLabel }
        : { "aria-hidden": true as const })
    })
}

export const IconArrowDown = ffnIcon(ArrowDownBase)
export const IconArrowDownRight = ffnIcon(ArrowDownRightBase)
export const IconArrowUp = ffnIcon(ArrowUpBase)
export const IconArrowUpRight = ffnIcon(ArrowUpRightBase)
export const IconCalendar = ffnIcon(CalendarBase)
export const IconCheck = ffnIcon(CheckBase)
export const IconChevronDown = ffnIcon(ChevronDownBase)
export const IconChevronLeft = ffnIcon(ChevronLeftBase)
export const IconChevronRight = ffnIcon(ChevronRightBase)
export const IconChevronsUpDown = ffnIcon(ChevronsUpDownBase)
export const IconHelpCircle = ffnIcon(CircleQuestionMarkBase)
export const IconMaximize = ffnIcon(MaximizeBase)
export const IconMenu2 = ffnIcon(MenuBase)
export const IconMinimize = ffnIcon(MinimizeBase)
export const IconMinus = ffnIcon(MinusBase)
export const IconMoon = ffnIcon(MoonBase)
export const IconPlus = ffnIcon(PlusBase)
export const IconQuote = ffnIcon(QuoteBase)
export const IconSearch = ffnIcon(SearchBase)
export const IconSun = ffnIcon(SunBase)
export const IconUpload = ffnIcon(UploadBase)
export const IconX = ffnIcon(XBase)
